// The heroes in captivity, in the mission tab (`heroes.captivity`).
//
// A captive hero is off the board — no hex, no unit, not drawn — so these
// two lists are where their absence is shown: the heroes you hold, and the
// heroes taken from your house.

import { useState } from 'react';
import {
  type HeroCaptive,
  type HeroCaptivity,
  captivesHeld,
  captivesTaken,
  heroUnitId,
  mayPayHeroRansom,
} from '../../heroes/captivity.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import type { CaptureChoice } from '../../sim/useSimWorld/applyIntent.js';
import { CaptorDialog } from '../Modal/CaptorDialog.js';
import { decisionLabel } from './captureDecision.js';

export interface HeroCaptivitySectionsProps {
  /** The heroes in captivity, while `heroes.captivity` is on. */
  readonly heroCaptivity?: HeroCaptivity | null;
  readonly localPlayerIndex: number;
  /** Decide what happens to a hero you hold. */
  readonly onChooseHeroCaptivity?: (unitId: string, choice: CaptureChoice) => boolean;
  /** Buy a hero of your house back. */
  readonly onPayHeroRansom?: (unitId: string) => boolean;
}

/** The heroes you hold, and the decision you have made about them. */
export function HeroHeldSection({
  heroCaptivity,
  localPlayerIndex,
  onChooseHeroCaptivity,
}: HeroCaptivitySectionsProps) {
  const t = useT();
  const heroHeld = captivesHeld(heroCaptivity ?? null, localPlayerIndex);
  if (heroHeld.length === 0) return null;
  return (
    <section className="flex flex-col gap-1.5" data-testid="heroes-held">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {t('heroes.captives')}
      </h3>
      {heroHeld.map((captive) => (
        <HeroCaptiveRow key={captive.id} captive={captive} onChoose={onChooseHeroCaptivity} />
      ))}
    </section>
  );
}

/** The heroes of your house that another house holds. */
export function HeroTakenSection({
  heroCaptivity,
  localPlayerIndex,
  onPayHeroRansom,
}: HeroCaptivitySectionsProps) {
  const t = useT();
  const heroTaken = captivesTaken(heroCaptivity ?? null, localPlayerIndex);
  if (heroTaken.length === 0) return null;
  return (
    <section className="flex flex-col gap-1.5" data-testid="heroes-taken">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {t('heroes.taken')}
      </h3>
      {heroTaken.map((captive) => {
        const canPay = mayPayHeroRansom(heroCaptivity ?? null, localPlayerIndex, captive.id);
        return (
          <div
            key={captive.id}
            className="flex flex-col gap-1 rounded-md border border-border/50 bg-card/60 px-2.5 py-2"
          >
            <div className="flex items-center gap-2 text-xs">
              <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                {captive.name}
              </span>
              <span className="shrink-0 text-[10px] text-muted-foreground">
                {t('heroes.level', { n: captive.level })}
              </span>
              <span className="shrink-0 text-[10px] text-muted-foreground">
                {t('heroes.heldSince', { n: captive.since })}
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {canPay && onPayHeroRansom && (
                <button
                  type="button"
                  data-testid={`pay-hero-ransom-${captive.id}`}
                  {...appDom({
                    id: `pay-hero-ransom-${captive.id}`,
                    type: 'Button',
                    role: 'button',
                    scope: 'section',
                  })}
                  onClick={() => onPayHeroRansom(heroUnitId(captive))}
                  className="rounded border border-amber-300/50 px-2 py-0.5 text-[10px] text-amber-100 hover:bg-amber-300/10"
                >
                  {t('heroes.payRansom')} ({captive.price})
                </button>
              )}
              {!canPay && (
                <span className="self-center text-[10px] text-muted-foreground">
                  {t('heroes.noRansom')}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}

/** A hero in your keeping, and the decision you have made about them. */
function HeroCaptiveRow({
  captive,
  onChoose,
}: {
  readonly captive: HeroCaptive;
  readonly onChoose?: (unitId: string, choice: CaptureChoice) => boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border/50 bg-card/60 px-2.5 py-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="min-w-0 flex-1 truncate font-medium text-foreground">{captive.name}</span>
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {t('heroes.level', { n: captive.level })}
        </span>
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {t(decisionLabel(captive.choice))}
        </span>
      </div>
      {onChoose && (
        <button
          type="button"
          data-testid={`hero-captor-decide-${captive.id}`}
          {...appDom({
            id: `hero-captor-decide-${captive.id}`,
            type: 'Button',
            role: 'button',
            scope: 'section',
          })}
          onClick={() => setOpen(true)}
          className="self-start rounded border border-amber-300/50 px-2 py-0.5 text-[10px] text-amber-100 hover:bg-amber-300/10"
        >
          {t('heroes.decision')}
        </button>
      )}
      {open && (
        <CaptorDialog
          captiveName={captive.name}
          current={captive.choice === 'undecided' ? null : captive.choice}
          kind="hero"
          onChoose={(choice) => {
            onChoose?.(heroUnitId(captive), choice);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
